import * as React from 'react'

import { sanitizeHtmlValue } from '@/lib/element-schema-sanitizers'
import { sectionSchemaRegistry } from '@/lib/section-schema-registry'
import { cn } from '@/lib/utils'

import { PageHeaderHeading } from '@/components/common/page-header'
import { Shell } from '@/components/common/shell'
import NextImage from '@/components/NextImage'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

import type { SectionElementSchema } from '@/types/element-schema.type'

interface FeatureItem {
  title: string
  subtitle: string
  bullets: string[] | Array<{ text: string }> | string
  image: string
  isComingSoon?: boolean
}

/** Feature item after normalization — bullets is always string[] */
interface NormalizedFeatureItem extends Omit<FeatureItem, 'bullets'> {
  bullets: string[]
}

/**
 * Normalize feature items from Puck editor.
 * Puck array fields with children produce objects like {text: '...'} instead of plain strings.
 */
function normalizeFeatures(features: FeatureItem[]): NormalizedFeatureItem[] {
  return features.map(feature => {
    let bullets: string[]
    if (Array.isArray(feature.bullets)) {
      bullets = feature.bullets.map(item => (typeof item === 'string' ? item : (item as { text: string }).text || ''))
    } else if (typeof feature.bullets === 'string' && feature.bullets) {
      bullets = [feature.bullets]
    } else {
      bullets = []
    }
    return { ...feature, bullets }
  })
}

interface FeaturePoweredSectionProps {
  className?: string
  /** Section heading (supports HTML) */
  heading?: string
  /** Heading text color */
  headingColor?: string
  /** Subtitle text color */
  subtitleColor?: string
  /** Bullet text color */
  bulletColor?: string
  /** Background color */
  bgColor?: string
  /** Card background color */
  cardBgColor?: string
  /** Data source: 'built-in' or 'custom' */
  dataSource?: 'built-in' | 'custom'
  /** Override default features (custom mode) */
  features?: FeatureItem[]
  /** Grid columns */
  columns?: 1 | 2
  /** Show coming soon badges */
  showComingSoon?: boolean
}

// Default features data
const defaultFeatures: FeatureItem[] = [
  {
    title: 'Hệ thống ôn tập hiệu quả',
    subtitle: 'Efficient Review System',
    bullets: ['Xem lại recording trực tiếp trên platform', 'Làm quiz, code homework, và nộp final project online'],
    image: '/images/home/feature-powered/quiz.png',
    isComingSoon: false,
  },
  {
    title: 'AI Teaching Assistant',
    subtitle: 'AI-Powered Teaching Support',
    bullets: [
      'Phân tích và trả lời câu hỏi bằng LLM',
      'Review & chấm code tự động, gợi ý cải thiện logic và hiệu năng',
      '<b>Coming soon:</b> Comment & review code with AI',
    ],
    image: '/images/home/feature-powered/ai-assistant.png',
    isComingSoon: true,
  },
  {
    title: 'Sandbox thực chiến',
    subtitle: 'Hands-on Sandbox',
    bullets: [
      'Triển khai dự án industry-ready ngay trên môi trường dựng sẵn',
      'Không cần cài đặt, có thể thử nghiệm và debug trực tiếp',
    ],
    image: '/images/home/feature-powered/sandbox.png',
    isComingSoon: false,
  },
  {
    title: 'AI Research & Career Agent',
    subtitle: 'Research & Career AI Support',
    bullets: [
      'Theo dõi và cập nhật các nghiên cứu, báo cáo mới nhất về Data & AI',
      'Gợi ý cơ hội nghề nghiệp, tin tuyển dụng phù hợp',
    ],
    image: '/images/home/hot-job.png',
    isComingSoon: true,
  },
]

// Element schema for Puck editor
export const featurePoweredSectionSchema: SectionElementSchema = {
  componentType: 'FeaturePoweredSection',
  label: 'Feature Powered Section',
  category: 'content',
  description: 'Display features in a card grid layout',
  elements: [
    // ── Section-level settings ──
    {
      key: 'heading',
      type: 'textarea',
      label: 'Section Heading',
      helpText: 'Main heading (supports HTML, use <span> for styling)',
      default: 'Học với <span class="text-primary">AI-powered e-learning platform</span> độc quyền',
    },
    {
      key: 'headingColor',
      type: 'color',
      label: 'Heading Color',
      helpText: 'Leave empty for default theme color',
      default: '',
    },
    {
      key: 'bgColor',
      type: 'color',
      label: 'Section Background Color',
      default: 'transparent',
    },
    // ── Card-level settings ──
    {
      key: 'cardBgColor',
      type: 'color',
      label: 'Card Background Color',
      helpText: 'Background color for each feature card',
      default: '',
    },
    {
      key: 'subtitleColor',
      type: 'color',
      label: 'Subtitle Color',
      helpText: 'Color for feature subtitle text',
      default: '',
    },
    {
      key: 'bulletColor',
      type: 'color',
      label: 'Bullet Text Color',
      helpText: 'Color for bullet point text',
      default: '',
    },
    // ── Layout ──
    {
      key: 'columns',
      type: 'select',
      label: 'Grid Columns',
      helpText: 'Number of columns in the feature grid',
      options: [
        { label: '1 Column', value: 1 },
        { label: '2 Columns', value: 2 },
      ],
      default: 2,
    },
    {
      key: 'showComingSoon',
      type: 'boolean',
      label: 'Show Coming Soon Badges',
      helpText: 'Display "Coming soon" badges on marked features',
      default: true,
    },
    // ── Data source ──
    {
      key: 'dataSource',
      type: 'select',
      label: 'Data Source',
      options: [
        { label: 'Built-in (Default)', value: 'built-in' },
        { label: 'Custom Items', value: 'custom' },
      ],
      default: 'built-in',
    },
    // ── Custom feature items ──
    {
      key: 'features',
      type: 'array',
      label: 'Custom Features',
      helpText: 'Add, edit, or remove feature cards (only when Data Source is "Custom")',
      default: defaultFeatures,
      children: [
        { key: 'title', type: 'text', label: 'Card Title', required: true, default: 'New Feature' },
        { key: 'subtitle', type: 'text', label: 'Card Subtitle', default: 'Feature subtitle' },
        {
          key: 'bullets',
          type: 'array',
          label: 'Bullet Points',
          helpText: 'Description points (supports HTML)',
          children: [{ key: 'text', type: 'textarea', label: 'Bullet Text (HTML)', default: 'Description point' }],
        },
        { key: 'image', type: 'image', label: 'Feature Image', default: '/images/home/feature-powered/quiz.png' },
        { key: 'isComingSoon', type: 'boolean', label: 'Coming Soon Badge', default: false },
      ],
    },
    {
      key: 'className',
      type: 'text',
      label: 'CSS Class',
      helpText: 'Additional CSS classes',
    },
  ],
}

// Register schema
sectionSchemaRegistry.register(featurePoweredSectionSchema)

/** Placeholder for missing images */
const ImagePlaceholder = () => (
  <div className="w-full aspect-[16/9] bg-gray-100 rounded-lg flex items-center justify-center">
    <div className="text-center text-gray-400">
      <svg className="w-10 h-10 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z"
        />
      </svg>
      <span className="text-xs">No image</span>
    </div>
  </div>
)

function FeaturePoweredSection({
  className,
  heading = 'Học với <span class="text-primary">AI-powered e-learning platform</span> độc quyền',
  headingColor,
  subtitleColor,
  bulletColor,
  bgColor = 'transparent',
  cardBgColor,
  dataSource = 'built-in',
  features: customFeatures,
  columns = 2,
  showComingSoon = true,
}: FeaturePoweredSectionProps) {
  const rawFeatures = dataSource === 'custom' && customFeatures?.length ? customFeatures : defaultFeatures
  const features = normalizeFeatures(rawFeatures)

  const gridCols = Number(columns) === 1 ? 'grid grid-cols-1' : 'grid grid-cols-1 md:grid-cols-2'

  return (
    <Shell
      className={cn('px-4 sm:px-6 md:px-8 py-8 sm:py-12 md:py-16 lg:py-20', className)}
      style={{ backgroundColor: bgColor !== 'transparent' ? bgColor : undefined }}
    >
      <div className="space-y-12">
        <PageHeaderHeading
          as="h2"
          size="lg"
          className="tracking-normal text-3xl sm:text-4xl md:text-5xl lg:text-5xl/tight md:leading-tight text-center"
          style={headingColor ? { color: headingColor } : undefined}
          dangerouslySetInnerHTML={{ __html: sanitizeHtmlValue(heading) }}
        />

        <div className={cn('gap-6 lg:gap-8', gridCols)}>
          {features.map((feature, index) => (
            <Card
              key={index}
              className={cn(
                'group relative overflow-hidden transition-all duration-300 hover:shadow-lg hover:-translate-y-1'
              )}
              style={cardBgColor ? { backgroundColor: cardBgColor } : undefined}
            >
              <CardHeader className="pb-4">
                <CardTitle className="text-xl md:text-2xl font-bold text-gray-900 mb-1">
                  {feature.title || 'Untitled Feature'}
                </CardTitle>
                <p
                  className="text-sm text-gray-600 font-medium"
                  style={subtitleColor ? { color: subtitleColor } : undefined}
                >
                  {feature.subtitle || ''}{' '}
                  {showComingSoon && feature.isComingSoon && (
                    <span className="text-primary font-medium italic"> (Coming soon)</span>
                  )}
                </p>
              </CardHeader>

              <CardContent className="space-y-4">
                {feature.bullets.length > 0 && (
                  <ul className="space-y-2">
                    {feature.bullets.map((bullet, bulletIndex) => (
                      <li
                        key={bulletIndex}
                        className="flex items-start text-sm text-gray-700"
                        style={bulletColor ? { color: bulletColor } : undefined}
                      >
                        <span className="inline-block w-1.5 h-1.5 bg-primary rounded-full mt-2 mr-3 flex-shrink-0" />
                        <span dangerouslySetInnerHTML={{ __html: sanitizeHtmlValue(bullet) }} />
                      </li>
                    ))}
                  </ul>
                )}

                <div className="relative mt-6 rounded-lg overflow-hidden bg-white/50 backdrop-blur-sm border border-white/20 mx-auto w-full max-w-[620px]">
                  {feature.image ? (
                    <NextImage
                      src={feature.image}
                      alt={feature.title || 'Feature image'}
                      className="w-full h-full object-cover"
                      width={624}
                      height={375}
                      quality={100}
                      sizes="100vw"
                      classNames={{
                        image: 'select-none object-cover',
                      }}
                    />
                  ) : (
                    <ImagePlaceholder />
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </Shell>
  )
}

export default FeaturePoweredSection
